import { useState, useCallback, useEffect, useRef } from 'react'
import TypesenseClient from '@/search/Typesense'
import { DiscoverieSearchResponseRecord } from '@/search/types'
import {
  useDiscoveryProductQuery,
  useEbookImageUrlQuery,
} from '@/generated/graphql'

/**
 * Fires once per resolved search (including the empty-purchased-library short-circuit).
 * The payload reflects the params that produced these results, so callers can attribute
 * tracking events correctly even if URL params change while a request is in flight.
 */
export interface DiscoverieSearchCompletionInfo {
  query: string
  page: number
  limit: number
  sortBy?: string
  totalCount: number
  purchasedProductIds?: string
  discoverieProductId?: string
  purchasedLibraryEmpty: boolean
}

const DISCOVERIE_SEARCH_PROVIDER = new TypesenseClient()
const COLLECTION = 'ebooks'
const DEFAULT_INCLUDE_FIELDS = [
  'object_id',
  'product_id',
  'image_filename',
  'title',
  'author',
  'page_number',
  'text',
  'download_file_id',
  'download_file_name',
]

interface UseDiscoverieSearchProps {
  query: string
  sortBy?: string
  pagination?: {
    page: number
    limit: number
  }
  onError?: (error: Error) => void
  includeFields?: string[]
  /** Typesense filter: comma-separated product IDs (my purchases only). */
  purchasedProductIds?: string
  /** Typesense filter: exact product ID for one Discoverie book. */
  discoverieProductId?: string
  /** My purchases only, but the account has no purchases — return no results without calling Typesense. */
  purchasedLibraryEmpty?: boolean
  /** If false, the search effect is skipped (e.g. while purchase IDs are loading). */
  enabled?: boolean
  /**
   * Called after each resolved search (success or empty-library short-circuit) with the
   * exact params that were searched. Use for analytics so events attribute to the right
   * query even when URL params change mid-flight.
   */
  onSearchComplete?: (info: DiscoverieSearchCompletionInfo) => void
}

export function useDiscoverieSearch({
  query,
  sortBy,
  pagination,
  onError,
  includeFields = DEFAULT_INCLUDE_FIELDS,
  purchasedProductIds,
  discoverieProductId,
  purchasedLibraryEmpty = false,
  enabled = true,
  onSearchComplete,
}: UseDiscoverieSearchProps) {
  const page = pagination?.page ?? 1
  const limit = pagination?.limit ?? 25

  const [results, setResults] = useState<DiscoverieSearchResponseRecord[]>([])
  const [totalCount, setTotalCount] = useState(0)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<Error | null>(null)

  // Hold the latest callback in a ref so changing its identity does not invalidate searchDiscoverie.
  const onSearchCompleteRef = useRef(onSearchComplete)
  useEffect(() => {
    onSearchCompleteRef.current = onSearchComplete
  }, [onSearchComplete])

  const clearResults = useCallback(() => {
    setResults([])
    setError(null)
  }, [])

  const enhanceSearchResult = useCallback(
    async (
      hit: DiscoverieSearchResponseRecord
    ): Promise<DiscoverieSearchResponseRecord> => {
      if (!hit.objectId) return hit

      try {
        const result = await useDiscoveryProductQuery.fetcher({
          id: String(hit.objectId),
          brandCode: 'VANISHING_INC_US',
        })()

        // Get the transformed image URL for the first page if it exists
        let transformedImageUrl: string | undefined
        if (
          result?.productForDiscoverie &&
          hit.pages?.length &&
          hit.pages.length > 1
        ) {
          transformedImageUrl =
            result?.productForDiscoverie?.imageUrl || undefined
        } else {
          if (hit.pages?.[0]?.image) {
            const imageUrlResult = await useEbookImageUrlQuery.fetcher({
              imagePath: hit.pages[0].image,
            })()
            transformedImageUrl = imageUrlResult?.ebookImageURL
          }
        }

        return {
          ...hit,
          productUrl:
            result?.productForDiscoverie?.productUrl || hit.productUrl,
          price: result?.productForDiscoverie?.price,
          hasPurchased: result?.productForDiscoverie?.hasPurchased,
          productThumbnail: transformedImageUrl,
          pages: hit.pages,
        }
      } catch {
        return { ...hit, hasPurchased: false as const }
      }
    },
    []
  )

  const searchDiscoverie = useCallback(async () => {
    setIsLoading(true)
    setError(null)

    try {
      if (purchasedLibraryEmpty) {
        setResults([])
        setTotalCount(0)
        onSearchCompleteRef.current?.({
          query,
          page,
          limit,
          sortBy,
          totalCount: 0,
          purchasedProductIds,
          discoverieProductId,
          purchasedLibraryEmpty: true,
        })
        return
      }

      const searchResults = await DISCOVERIE_SEARCH_PROVIDER.searchEbooks({
        collection: COLLECTION,
        query,
        sortBy,
        includeFields,
        page,
        limit,
        purchasedProductIds,
        discoverieProductId,
      })

      const enhancedResults = await Promise.all(
        (searchResults.groupedHits || []).map(enhanceSearchResult)
      )

      setResults(enhancedResults)
      setTotalCount(searchResults.totalCount)
      onSearchCompleteRef.current?.({
        query,
        page,
        limit,
        sortBy,
        totalCount: searchResults.totalCount,
        purchasedProductIds,
        discoverieProductId,
        purchasedLibraryEmpty: false,
      })
    } catch (err) {
      const error = err instanceof Error ? err : new Error('Search failed')
      setResults([])
      setTotalCount(0)
      setError(error)
      onError?.(error)
    } finally {
      setIsLoading(false)
    }
  }, [
    enhanceSearchResult,
    includeFields,
    limit,
    onError,
    page,
    discoverieProductId,
    purchasedLibraryEmpty,
    purchasedProductIds,
    query,
    sortBy,
  ])

  useEffect(() => {
    if (query && enabled) {
      queueMicrotask(() => {
        void searchDiscoverie()
      })
    }
  }, [query, searchDiscoverie, enabled])

  return {
    searchDiscoverie,
    results,
    isLoading,
    error,
    totalCount,
    clearResults,
  }
}
